import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatErr } from "../../lib/api";
import { usePolling } from "../../lib/usePolling";
import { FilterBanner } from "../../components/FilterBanner";
import { ClientSelect } from "../../components/ClientSelect";
import { toast } from "sonner";
import { Plus, CheckCircle2, XCircle } from "lucide-react";

export default function AgentExtra() {
  const [list, setList] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", contact_id: null, salon_name: "" });
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [sp, setSp] = useSearchParams();
  const onlyPreventivo = sp.get("filter") === "preventivo";

  const load = async () => {
    try { const { data } = await http.get("/crm/me/extras"); setList(data); setBlocked(false); }
    catch (e) { if (e?.response?.status === 403) { setBlocked(true); setList([]); } else toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  usePolling(load, { interval: 10000, enabled: !blocked });

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return toast.error("Titolo e descrizione richiesti.");
    try { await http.post("/crm/me/extras", form); setShow(false); setForm({ title: "", description: "", contact_id: null, salon_name: "" }); await load(); toast.success("Richiesta extra inviata."); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const decide = async (id, decision, r) => {
    try { await http.post(`/crm/me/extras/${id}/decision`, { decision, reason: r }); setRejecting(null); setReason(""); await load(); toast.success(decision === "accettata" ? "Preventivo accettato." : "Preventivo rifiutato."); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (blocked) return <div data-testid="agent-extra-blocked" className="max-w-2xl mx-auto card-clean p-6 text-center"><h1 className="font-serif text-2xl mb-2">Extra</h1><p className="text-sm text-neutral-600">Disponibili dopo l'attivazione.</p></div>;
  if (list === null) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;
  const shown = onlyPreventivo ? list.filter((e) => e.state === "preventivo_inviato") : list;

  return (
    <div data-testid="agent-extra-page" className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-serif text-3xl">Richieste Extra</h1>
        <button onClick={() => setShow((v) => !v)} data-testid="agent-extra-new" className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2" style={{ background: "var(--brand-dark)" }}><Plus className="w-4 h-4" /> Nuova richiesta</button>
      </div>
      <FilterBanner label={onlyPreventivo ? "Preventivo da decidere" : null} onClear={() => setSp({})} />
      {show && (
        <form onSubmit={create} className="card-clean p-5 mb-5 space-y-3 text-sm" data-testid="agent-extra-form">
          <input placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ae-title" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
          <div><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Cliente collegato (opz.)</span><ClientSelect testid="ae-client" value={{ contact_id: form.contact_id, salon_name: form.salon_name }} onChange={(v) => setForm({ ...form, contact_id: v.contact_id, salon_name: v.salon_name })} /></div>
          <textarea placeholder="Descrizione libera della lavorazione extra" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ae-desc" rows={3} className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
          <button type="submit" data-testid="agent-extra-save" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>Invia richiesta</button>
        </form>
      )}
      <div className="space-y-3" data-testid="agent-extra-list">
        {shown.length === 0 && <div className="card-clean p-6 text-sm text-neutral-500">Nessuna richiesta extra{onlyPreventivo ? " con preventivo da decidere" : ""}.</div>}
        {shown.map((e) => (
          <div key={e.id} className="card-clean p-4" data-testid={`agent-extra-${e.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1">
                <div className="font-semibold">{e.title} <span className="text-[11px] text-neutral-400">· {e.state}</span></div>
                <div className="text-xs text-neutral-500">{e.description}</div>
                {e.quote && <div className="text-xs mt-1">Preventivo: € {e.quote.amount_net} (acconto € {e.quote.acconto_net} + saldo € {e.quote.saldo_net})</div>}
                {e.decision_reason && <div className="text-xs text-rose-600 mt-1">Rifiutato: {e.decision_reason}</div>}
              </div>
              {e.state === "preventivo_inviato" && rejecting !== e.id && (
                <div className="flex gap-2">
                  <button onClick={() => decide(e.id, "accettata")} data-testid={`agent-extra-accept-${e.id}`} className="px-3 py-1.5 rounded-md text-white text-xs flex items-center gap-1" style={{ background: "var(--brand-dark)" }}><CheckCircle2 className="w-3 h-3" /> Accetta</button>
                  <button onClick={() => setRejecting(e.id)} data-testid={`agent-extra-reject-${e.id}`} className="px-3 py-1.5 rounded-md border text-xs flex items-center gap-1 text-rose-600" style={{ borderColor: "var(--border-silver)" }}><XCircle className="w-3 h-3" /> Rifiuta</button>
                </div>
              )}
            </div>
            {rejecting === e.id && (
              <div className="flex gap-2 mt-3" data-testid={`agent-extra-reject-box-${e.id}`}>
                <input placeholder="Motivazione del rifiuto" value={reason} onChange={(ev) => setReason(ev.target.value)} data-testid={`agent-extra-reason-${e.id}`} className="flex-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }} />
                <button onClick={() => decide(e.id, "rifiutata", reason)} data-testid={`agent-extra-reject-confirm-${e.id}`} className="px-3 py-2 rounded-md text-white text-xs" style={{ background: "#dc2626" }}>Conferma rifiuto</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
